import { useState, useMemo, useEffect } from 'react';
import { CommentHistory } from './CommentHistory';
import {
  useHoldRequestComments,
  useLineItemComments,
} from '@/hooks/useComments';

interface PaginatedCommentHistoryProps {
  holdRequestId?: string;
  lineItemId?: string;
  pageSize?: number;
  enableCollapsible?: boolean;
  initialCollapsedCount?: number;
  recentThresholdMinutes?: number;
}

/**
 * PaginatedCommentHistory Component
 * 
 * A wrapper around CommentHistory that handles pagination automatically.
 * Fetches comments from the API with pagination support and manages page state.
 * Tracks last viewed timestamp for unread comment highlighting.
 */
export function PaginatedCommentHistory({
  holdRequestId,
  lineItemId,
  pageSize = 20,
  enableCollapsible = true,
  initialCollapsedCount = 5,
  recentThresholdMinutes = 5,
}: PaginatedCommentHistoryProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [lastViewedTimestamp, setLastViewedTimestamp] = useState<Date | null>(null);

  // Load last viewed timestamp from localStorage on mount
  useEffect(() => {
    const storageKey = holdRequestId
      ? `comment-last-viewed-hold-${holdRequestId}`
      : `comment-last-viewed-item-${lineItemId}`;
    
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        setLastViewedTimestamp(new Date(stored));
      } catch {
        // Invalid date, ignore
      }
    }
  }, [holdRequestId, lineItemId]);

  // Save last viewed timestamp to localStorage
  const handleViewedTimestampUpdate = (timestamp: Date) => {
    const storageKey = holdRequestId
      ? `comment-last-viewed-hold-${holdRequestId}`
      : `comment-last-viewed-item-${lineItemId}`;
    
    localStorage.setItem(storageKey, timestamp.toISOString());
    setLastViewedTimestamp(timestamp);
  };

  // Calculate offset based on current page
  const offset = (currentPage - 1) * pageSize;

  // Fetch hold request comments if holdRequestId is provided
  const {
    data: holdRequestData,
    isLoading: holdRequestLoading,
    error: holdRequestError,
  } = useHoldRequestComments(
    holdRequestId || '',
    {
      limit: pageSize,
      offset,
      sortOrder: 'desc',
    }
  );

  // Fetch line item comments if lineItemId is provided
  const {
    data: lineItemData,
    isLoading: lineItemLoading,
    error: lineItemError,
  } = useLineItemComments(
    lineItemId || '',
    {
      limit: pageSize,
      offset,
      sortOrder: 'desc',
    }
  );

  // Determine which data to use
  const data = holdRequestId ? holdRequestData : lineItemData;
  const isLoading = holdRequestId ? holdRequestLoading : lineItemLoading;
  const error = holdRequestId ? holdRequestError : lineItemError;

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!data?.totalCount) return 1;
    return Math.ceil(data.totalCount / pageSize);
  }, [data?.totalCount, pageSize]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top of comments section
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Determine if pagination should be shown
  const showPagination = (data?.totalCount || 0) > pageSize;

  return (
    <CommentHistory
      comments={data?.comments || []}
      loading={isLoading}
      error={error ? 'Failed to load comments. Please try again.' : null}
      emptyMessage="No comments yet. Be the first to add one!"
      showPagination={showPagination}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={data?.totalCount || 0}
      onPageChange={handlePageChange}
      pageSize={pageSize}
      enableCollapsible={enableCollapsible}
      initialCollapsedCount={initialCollapsedCount}
      lastViewedTimestamp={lastViewedTimestamp}
      recentThresholdMinutes={recentThresholdMinutes}
      onViewedTimestampUpdate={handleViewedTimestampUpdate}
    />
  );
}
